import React, { FC, KeyboardEvent, ReactNode } from 'react';

import styled from '@emotion/styled';
import {
  compose,
  variant,
  space,
  display,
  flex,
  layout,
  DisplayProps,
  FlexProps,
  LayoutProps,
  SpaceProps,
  ComponentsType
} from '@lucid/theme';

import CardAction, { CardActionProps } from './card-action';
import CardActions, { CardActionsProps } from './card-actions';
import CardContent, { CardContentProps } from './card-content';
import CardImage, { CardImageProps } from './card-image';
import CardSubtitle, { CardSubtitleProps } from './card-subtitle';
import CardTitle, { CardTitleProps } from './card-title';
import decorate from '../decorate/decorate';

export interface CardSubComponents {
  /** The Action Subcomponent for Card. */
  Action: FC<CardActionProps>;
  /** The Actions Subcomponent for Card. */
  Actions: FC<CardActionsProps>;
  /** The Content Subcomponent for Card. */
  Content: FC<CardContentProps>;
  /** The Image Subcomponent for Card. */
  Image: FC<CardImageProps>;
  /** The Subtitle Subcomponent for Card. */
  Subtitle: FC<CardSubtitleProps>;
  /** The Title Subcomponent for Card. */
  Title: FC<CardTitleProps>;
}

export interface CardProps
  extends DisplayProps,
  FlexProps,
  LayoutProps,
  SpaceProps {
  /** Sets the HTML tag for the Card component. */
  as?: 'div' | 'section' | 'article';
  /** The Card component children. */
  children?: ReactNode;
  /** Allows the component to get additional styles. */
  className?: string;
  /** Sets the approriate styles if disabled. */
  isDisabled?: boolean;
  /** Passes an onClick function to the Card component. */
  onClick?: () => void;
  /** Passes an onKeyDown function to the Card component. */
  onKeyDown?: (event: KeyboardEvent<Element>) => void;
  /** Sets the role of the Card component. */
  role?: string;
  /** Sets the tabIndex for the Box Component. */
  tabIndex?: number;
  /** The available variants for the Card component. */
  variant?: ComponentsType['Cards']['Card'];
}

const cardStyleProps = compose(
  space,
  display,
  layout,
  flex
);

const StyledCard: FC<CardProps> = styled.div(
  ({
    theme,
    isDisabled
  }) => ([
    {
      label: 'card',
      backgroundColor: theme.colors.white,
      borderRadius: theme.radii.card,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: theme.colors.coolGrey[10],
      overflow: 'hidden'
    },
    isDisabled && {
      borderColor: theme.colors.coolGrey[5],
      pointerEvents: 'none'
    }
  ]),
  variant({
    scale: 'cards.card',
    // required to enable the new API
    variants: {
      defaultVariant: {}
    }
  }),
  cardStyleProps
);

const CardComponent: FC<CardProps> & CardSubComponents = ({ children, ...rest }) => (
  <StyledCard {...rest}>
    {children}
  </StyledCard>
);

CardComponent.Action = CardAction;
CardComponent.Actions = CardActions;
CardComponent.Content = CardContent;
CardComponent.Image = CardImage;
CardComponent.Subtitle = CardSubtitle;
CardComponent.Title = CardTitle;

// manually adding display name or it goes missing on build
CardComponent.displayName = 'Card';
const Card = decorate<CardProps, CardSubComponents>(CardComponent);
export { Card };
export default Card;
